import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useRosterNurseName } from "../../../hooks/rosterCache";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import { backendService } from "../../../services/backend.service";
import type { CreateRecurringShiftRequest, DeleteRecurringShiftRequest, RecurringShift, UpdateRecurringShiftRequest } from "../../../utils/models";
import ViewProfileSnackbarAction from "../../ViewProfileSnackbarAction";

const RECURRING_SHIFTS_QUERY_KEY = (departmentId: string, nurseId?: number) => ["recurringShifts", departmentId, nurseId];

export function useGetRecurringShifts(departmentId: string, nurseId?: number) {
	const showErrorSnackbar = useErrorSnackbar();
	return useQuery<RecurringShift[]>({
		queryKey: RECURRING_SHIFTS_QUERY_KEY(departmentId, nurseId),
		queryFn: async () => {
			return nurseId ? (await backendService.getRecurringShifts(departmentId, nurseId)).data : [];
		},
		enabled: !!departmentId && !!nurseId, // only fetch if both are available
		refetchOnWindowFocus: false,
		gcTime: 0, // replaces keepPreviousData: false
		throwOnError: (error) => {
			showErrorSnackbar("Failed to fetch recurring shifts", { departmentId, nurseId }, error);
			return false;
		},
	});
}

const RECURRING_SHIFT_QUERY_KEY = (departmentId: string, nurseId?: number, recurringShiftId?: number) => [
	"recurringShift",
	departmentId,
	nurseId,
	recurringShiftId,
];

export function useGetRecurringShift(departmentId: string, nurseId?: number, recurringShiftId?: number) {
	const showErrorSnackbar = useErrorSnackbar();
	return useQuery<RecurringShift | null>({
		queryKey: RECURRING_SHIFT_QUERY_KEY(departmentId, nurseId, recurringShiftId),
		queryFn: async () => {
			return nurseId && recurringShiftId ? (await backendService.getRecurringShift(departmentId, nurseId, recurringShiftId)).data : null;
		},
		enabled: !!departmentId && !!nurseId && !!recurringShiftId, // only fetch if both are available
		refetchOnWindowFocus: false,
		gcTime: 0, // replaces keepPreviousData: false
		throwOnError: (error) => {
			showErrorSnackbar("Failed to fetch recurring shift", { departmentId, nurseId, recurringShiftId }, error);
			return false;
		},
	});
}

export function useCreateRecurringShift() {
	// Refreshing Staffing's data after the save, and the demo's shift insert, stay on the provider until step 5.
	const { afterScheduleWrite, addNewDBNurseShifts } = useHospitalData();
	const { isAnyDemo } = useDepartmentFlags();
	const showErrorSnackbar = useErrorSnackbar();
	const rosterNurseName = useRosterNurseName();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	return useMutation({
		mutationFn: async (request: CreateRecurringShiftRequest) => await backendService.postRecurringShift(request),
		onSuccess: async (data, variables) => {
			const result = data.data;
			if (isAnyDemo) {
				addNewDBNurseShifts(result.createdNurseShifts);
			} else {
				await afterScheduleWrite({ allWeeks: true });
			}
			const nurseId = variables.recurringShift.nurseId;
			const nurseName = rosterNurseName(variables.recurringShift.departmentId, nurseId);
			showSnackbar(
				nurseName ? t("recurring.shift.created.successfully.named", { nurseName }) : t("recurring.shift.created.successfully"),
				"success",
				undefined,
				nurseName ? <ViewProfileSnackbarAction to={`/${variables.recurringShift.departmentId}/nurse/${nurseId}`} /> : undefined,
			);
		},
		onError: (error, request) => {
			showErrorSnackbar("Failed to create recurring shift", request, error);
		},
	});
}

export function useUpdateRecurringShift() {
	// Refreshing Staffing's data after the save stays on the provider until step 5.
	const { afterScheduleWrite } = useHospitalData();
	const showErrorSnackbar = useErrorSnackbar();
	const rosterNurseName = useRosterNurseName();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	return useMutation({
		mutationFn: async (request: UpdateRecurringShiftRequest) => await backendService.putRecurringShift(request),
		onSuccess: async (_data, variables) => {
			await afterScheduleWrite({ allWeeks: true });
			const nurseId = variables.recurringShift.nurseId;
			const nurseName = rosterNurseName(variables.recurringShift.departmentId, nurseId);
			showSnackbar(
				nurseName ? t("recurring.shift.updated.successfully.named", { nurseName }) : t("recurring.shift.updated.successfully"),
				"success",
				undefined,
				nurseName ? <ViewProfileSnackbarAction to={`/${variables.recurringShift.departmentId}/nurse/${nurseId}`} /> : undefined,
			);
		},
		onError: (error, request) => {
			showErrorSnackbar("Failed to update recurring shift", request, error);
		},
	});
}

export function useDeleteRecurringShift() {
	// Refreshing Staffing's data after the save stays on the provider until step 5.
	const { afterScheduleWrite } = useHospitalData();
	const showErrorSnackbar = useErrorSnackbar();
	const rosterNurseName = useRosterNurseName();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	return useMutation({
		mutationFn: async (request: DeleteRecurringShiftRequest) => await backendService.deleteRecurringShift(request),
		onSuccess: async (_data, variables) => {
			await afterScheduleWrite({ allWeeks: true });
			const nurseId = variables.nurse_id;
			const nurseName = rosterNurseName(variables.department_id, nurseId);
			showSnackbar(
				nurseName ? t("recurring.shift.deleted.successfully.named", { nurseName }) : t("recurring.shift.deleted.successfully"),
				"success",
				undefined,
				nurseName ? <ViewProfileSnackbarAction to={`/${variables.department_id}/nurse/${nurseId}`} /> : undefined,
			);
		},
		onError: (error, request) => {
			showErrorSnackbar("Failed to delete recurring shift", request, error);
		},
	});
}
